import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Archive } from "lucide-react";

interface WithdrawCopyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  barcode: string;
  loading?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
}

export function WithdrawCopyDialog({
  open,
  onOpenChange,
  barcode,
  loading = false,
  onConfirm,
}: WithdrawCopyDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setReason("");
      setError(null);
    }
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("A withdrawal reason is required.");
      return;
    }
    setError(null);
    await onConfirm(trimmed);
    setReason("");
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]" onClose={() => handleOpenChange(false)}>
        <DialogHeader>
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-amber-100">
              <Archive className="h-6 w-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Withdraw Copy
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-gray-600">
                Withdraw <span className="font-mono font-medium">{barcode}</span> from circulation.
                It will be hidden from the OPAC and cannot be borrowed.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="withdrawal-reason">
            Reason <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="withdrawal-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder="e.g. Damaged beyond repair, duplicate copy, weeded from collection"
            rows={3}
            className="resize-none"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter className="mt-4 flex gap-3">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading}>
            {loading ? "Withdrawing..." : "Withdraw Copy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
